import React, { useMemo, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    Rating as StarRating,
    TableSortLabel,
    Button,
    TableFooter,
} from '@mui/material';

type DashboardListSortingProps = {
    title: string;
    tableTitle: { name: string; secondName: string };
    data: { name: string; value: number }[];
    metric?: { id: number; name: string };
};

export default function DashboardListSortingTable({ title, tableTitle, data, metric }: DashboardListSortingProps) {
    const totalValue = data?.reduce((sum, item) => sum + item.value, 0);
    const metricId = metric?.id;

    const rowPaddingY = metricId === 2 ? 1 : 0.6;
    const rowPaddingX = metricId === 2 ? 3 : 1.5;

    const [order, setOrder] = useState('desc'); // Usually better to default to descending for numeric values

    // 2. Toggle between 'asc' and 'desc' on click
    const handleRequestSort = () => {
        setOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
    };

    // 3. Sort the data strictly by the 'value' field
    const sortedData = useMemo(() => {
        if (!data) return [];

        return [...data].sort((a, b) => {
            const valueA = a.value;
            const valueB = b.value;

            // Handle null or undefined values gracefully
            if (valueA == null) return order === 'asc' ? 1 : -1;
            if (valueB == null) return order === 'asc' ? -1 : 1;

            // Numeric comparison
            return order === 'asc' ? valueA - valueB : valueB - valueA;
        });
    }, [data, order]);

    return (
        <Paper elevation={6} sx={{ p: 2, borderRadius: 3, height: '100%', overflow: 'hidden' }}>
            <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                {title}
            </Typography>
            <TableContainer
                sx={{
                    maxHeight: metricId === 4 ? 220 : 'none',
                    overflowY: metricId === 4 ? 'auto' : 'visible',
                }}
            >
                <Table
                    stickyHeader
                    size="small"
                    sx={{
                        '& .MuiTableCell-root': {
                            borderBottom: 'none',
                            fontSize: '0.92rem',
                            py: metricId === 3 ? 0.3 : rowPaddingY,
                            px: rowPaddingX,
                        },
                    }}
                >
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#eef3ff' }}>
                            <TableCell
                                sx={{
                                    py: 1.2,
                                    fontWeight: 600,
                                    color: '#1f3c88',
                                    borderRadius: '8px 0 0 8px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {tableTitle.name}
                            </TableCell>

                            <TableCell
                                align="right"
                                sx={{
                                    py: 1.2,
                                    fontWeight: 600,
                                    color: '#1f3c88',
                                    borderRadius: '0 8px 8px 0',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <TableSortLabel
                                    active // Always active since it is the only sortable column
                                    direction={order}
                                    onClick={handleRequestSort}
                                    sx={{
                                        color: '#1f3c88 !important',
                                        flexDirection: 'row', // Keeps the layout clean for right-aligned items
                                        '& .MuiTableSortLabel-icon': { color: '#1f3c88 !important' },
                                    }}
                                >
                                    {tableTitle.secondName}
                                </TableSortLabel>
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {sortedData.map((row) => (
                            <TableRow key={row.name} hover>
                                <TableCell sx={{ color: '#555', py: rowPaddingY, flexDirection: 'row' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="body2" sx={{ mr: 1 }}>
                                            {row.name}
                                        </Typography>
                                        {metricId === 3 &&
                                            (row.name === 'S' ? (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    sx={{ px: 1.25, py: 0.25, backgroundColor: '#2465dd' }}
                                                >
                                                    {row.name}
                                                </Button>
                                            ) : (
                                                <StarRating value={Number(row.name)} readOnly size="small" />
                                            ))}
                                    </Box>
                                </TableCell>
                                <TableCell align="right" sx={{ color: '#555', py: rowPaddingY }}>
                                    {row?.value?.toLocaleString()}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                    <TableFooter sx={{ position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#fff' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700, color: '#003399', borderTop: '2px solid #adc8ff' }}>Total</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#003399', borderTop: '2px solid #adc8ff' }}>
                                {totalValue.toLocaleString()}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Paper>
    );
}
